import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  Database,
  FlaskConical,
  Loader2,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useState } from "react";
import { AppLayout } from "../components/layout/AppLayout.js";
import { Header } from "../components/layout/Header.js";
import { employeesApi } from "../lib/api.js";

// Heuristische Basis-Gewichte aus retention-scoring.ts (zur Gegenüberstellung)
const BASE_WEIGHTS: Record<string, number> = {
  very_short_tenure: 0.18,
  short_tenure: 0.14,
  medium_tenure_risk: 0.04, // gesenkt 2026-07-06 (Retro-Test: invertiertes Signal)
  contract_ending_60: 0.3,
  contract_ending_180: 0.18,
  contract_expiring_year: 0.08,
  in_probation_ending: 0.14,
  in_probation: 0.07,
  no_manager: 0.1,
  onboarding: 0.08,
  on_leave: 0.09,
  part_time: 0.07,
  reduced_hours: 0.06,
  consulting_no_bereich: 0.07,
  tnw_track: 0.1,
  tnw_no_ypp_date: 0.04,
  long_tenure_anchor: 0.04,
  low_event_participation: 0.09,
  no_recent_event: 0.07,
  high_event_participation: 0.03,
  high_sick_days: 0.1,
  frequent_sick_episodes: 0.08,
  recent_sick_spike: 0.07,
  trainee_plateau: 0.14,
  professional_plateau: 0.1,
  senior_stagnation: 0.07,
};

const SIGNAL_LABELS: Record<string, string> = {
  very_short_tenure: "Betriebszugehörigkeit < 6 Monate",
  short_tenure: "Betriebszugehörigkeit 6–12 Monate",
  medium_tenure_risk: "Betriebszugehörigkeit 1–3 Jahre",
  long_tenure_anchor: "Betriebszugehörigkeit > 5 Jahre (Anker)",
  contract_ending_60: "Vertragsende < 60 Tage",
  contract_ending_180: "Vertragsende 60–180 Tage",
  contract_expiring_year: "Vertragsende 180–365 Tage",
  in_probation_ending: "Probezeit endet < 30 Tage",
  in_probation: "In Probezeit",
  no_manager: "Kein Vorgesetzter",
  onboarding: "Im Onboarding",
  on_leave: "Abwesend",
  part_time: "Teilzeit < 30h",
  reduced_hours: "Sehr reduziert < 20h",
  consulting_no_bereich: "Consulting ohne Team",
  tnw_track: "TNW-Talentnetzwerk",
  tnw_no_ypp_date: "TNW ohne YPP-Datum",
  low_event_participation: "Event-Teilnahme < 30%",
  no_recent_event: "Kein kürzliches Event",
  high_event_participation: "Hohe Event-Teilnahme (Anker)",
  high_sick_days: "Eigene Krankheitstage > 20/12M",
  frequent_sick_episodes: "Häufige eigene Kurzerkrankungen > 8/12M",
  recent_sick_spike: "Anstieg eigener Krankheitstage > 5/3M",
  trainee_plateau: "Trainee > 18M (Plateau)",
  professional_plateau: "Professional > 12M (Übergang)",
  senior_stagnation: "Senior > 4J (Stagnation)",
};

/**
 * Trainingsdaten-Fundament (Stufe 0): zeigt, wie viele Monats-Schnappschüsse
 * bereits gesammelt sind und wie viele Personio-Austritts-Flags noch zu pflegen
 * sind. Lädt sofort — unabhängig vom manuellen LR-Training.
 */
function ReadinessCard() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["training-readiness"],
    queryFn: employeesApi.getTrainingReadiness,
    staleTime: 10 * 60_000,
    retry: 0,
  });

  if (isLoading) {
    return (
      <div
        className="card p-4 mb-4 flex items-center gap-2 text-sm"
        style={{ color: "var(--text-muted)" }}
      >
        <Loader2 size={14} className="animate-spin" /> Lade Trainingsdaten-Status…
      </div>
    );
  }
  if (isError || !data) return null;

  const openFlags = data.regrettableUnassigned;
  const period =
    data.firstMonth && data.lastMonth
      ? data.firstMonth === data.lastMonth
        ? data.firstMonth
        : `${data.firstMonth} – ${data.lastMonth}`
      : "—";

  const stats = [
    {
      icon: CalendarClock,
      label: "Erfasste Monate",
      value: String(data.monthsCovered),
      sub: period,
    },
    {
      icon: Database,
      label: "Schnappschüsse",
      value: data.snapshotCount.toLocaleString("de-DE"),
      sub: `${data.distinctEmployees} Mitarbeiter`,
    },
    {
      icon: CheckCircle2,
      label: "Trainierbar (Ziel 1)",
      value: String(data.trainablePositives),
      sub: `${data.trainableNegatives} Gegenbeispiele`,
    },
    {
      icon: FlaskConical,
      label: "Noch offen (zensiert)",
      value: String(data.censored),
      sub: `Fenster ${data.horizonMonths} Monate`,
    },
  ];

  return (
    <div className="card p-4 mb-4">
      <div className="flex items-center gap-2 mb-1">
        <Database size={14} style={{ color: "var(--accent)" }} />
        <h2 className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
          Trainingsdaten-Fundament
        </h2>
      </div>
      <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>
        Ab sofort friert die Plattform jeden Monat den Zustand aller Mitarbeiter ein. Zusammen mit
        den in Personio gepflegten Austritts-Flags (bedauerlich + freiwillig) entsteht so mit der
        Zeit ein realistischer, datengetriebener Score.
      </p>

      <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
        {stats.map(({ icon: Icon, label, value, sub }) => (
          <div key={label} className="rounded p-3" style={{ background: "var(--bg-hover)" }}>
            <div className="flex items-center gap-1.5 mb-1">
              <Icon size={12} style={{ color: "var(--text-muted)" }} />
              <span
                className="text-[9px] uppercase tracking-widest font-semibold"
                style={{ color: "var(--text-muted)" }}
              >
                {label}
              </span>
            </div>
            <div
              className="text-xl font-bold font-mono-data"
              style={{ color: "var(--text-primary)" }}
            >
              {value}
            </div>
            <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>
              {sub}
            </div>
          </div>
        ))}
      </div>

      {/* Personio-Pflegestand der Austritts-Flags */}
      <div
        className="rounded p-3 flex items-start gap-2"
        style={
          openFlags > 0
            ? {
                background: "var(--risk-medium-bg, rgba(234,179,8,0.10))",
                border: "1px solid rgba(234,179,8,0.3)",
              }
            : {
                background: "var(--risk-low-bg, rgba(16,185,129,0.10))",
                border: "1px solid rgba(16,185,129,0.3)",
              }
        }
      >
        {openFlags > 0 ? (
          <AlertCircle size={14} style={{ color: "var(--risk-medium)", marginTop: 1 }} />
        ) : (
          <CheckCircle2 size={14} style={{ color: "var(--risk-low)", marginTop: 1 }} />
        )}
        <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
          <div className="font-semibold mb-0.5" style={{ color: "var(--text-primary)" }}>
            Personio-Austritts-Flags: {data.regrettableYes} bedauerlich · {data.regrettableNo} nicht
            bedauerlich · {openFlags} offen
          </div>
          {openFlags > 0 ? (
            <>
              Noch <strong>{openFlags}</strong> von {data.terminationsTotal} Austritten ohne
              gepflegtes „Bedauert"-Feld. Jeder in Personio nachgepflegte Flag verbessert die
              Label-Qualität und damit den Score.
            </>
          ) : (
            <>
              Alle {data.terminationsTotal} Austritte sind gepflegt. Die Label-Basis ist
              vollständig.
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function ModelValidation() {
  const [hasRun, setHasRun] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["lr-validation"],
    queryFn: employeesApi.getLrValidation,
    enabled: hasRun,
    staleTime: 30 * 60_000,
    retry: 0,
  });

  const coefficients = data?.coefficients ?? {};
  const signals = Object.keys({ ...BASE_WEIGHTS, ...coefficients }).filter(
    (k) => BASE_WEIGHTS[k] !== undefined,
  );

  // Normalize LR coefficients to 0-1 range for visual comparison
  const maxCoeff = Math.max(...Object.values(coefficients).map(Math.abs), 0.001);

  return (
    <AppLayout>
      <Header
        title="Modell-Validierung"
        subtitle="Logistische Regression vs. heuristische Gewichte · Admin only"
      />
      <div className="flex-1 overflow-y-auto px-6 py-5 page-enter">
        <ReadinessCard />

        {!hasRun && (
          <div className="card p-8 flex flex-col items-center gap-4 max-w-xl mx-auto mt-8">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(234,91,11,0.12)", color: "var(--accent)" }}
            >
              <FlaskConical size={22} />
            </div>
            <div className="text-center">
              <h2 className="text-base font-semibold mb-1" style={{ color: "var(--text-primary)" }}>
                Logistische Regression trainieren
              </h2>
              <p className="text-sm leading-relaxed" style={{ color: "var(--text-muted)" }}>
                Trainiert ein Regressionsmodell auf historischen Abgangsdaten und vergleicht die
                gelernten Koeffizienten mit den heuristischen Gewichten.
              </p>
            </div>
            <div
              className="w-full p-3 rounded text-xs leading-relaxed"
              style={{ background: "var(--bg-hover)", color: "var(--text-muted)" }}
            >
              <strong style={{ color: "var(--text-secondary)" }}>Methode:</strong> Gradient Descent
              (200 Epochen, L2 λ=0.01)
              <br />
              <strong style={{ color: "var(--text-secondary)" }}>Daten:</strong> Alle Mitarbeiter —
              Inaktive = positiv (1), Aktive = negativ (0)
              <br />
              <strong style={{ color: "var(--text-secondary)" }}>Zweck:</strong> Validierung und
              Kalibrierung, keine Echtzeit-Vorhersage
            </div>
            <button
              type="button"
              className="flex items-center gap-2 px-5 py-2.5 rounded text-sm font-semibold"
              style={{ background: "var(--accent)", color: "#fff" }}
              onClick={() => {
                setHasRun(true);
                void refetch();
              }}
            >
              <FlaskConical size={14} />
              Training starten
            </button>
          </div>
        )}

        {isLoading && (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <Loader2 size={20} className="animate-spin" style={{ color: "var(--accent)" }} />
            <p className="text-xs font-mono-data" style={{ color: "var(--text-muted)" }}>
              TRAINING LÄUFT — 200 EPOCHEN…
            </p>
          </div>
        )}

        {isError && (
          <div
            className="flex items-center gap-2 p-4 rounded-lg"
            style={{ background: "var(--risk-high-bg)", border: "1px solid rgba(240,78,78,0.2)" }}
          >
            <AlertCircle size={14} style={{ color: "var(--risk-high)" }} />
            <span className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {error instanceof Error ? error.message : "Training fehlgeschlagen"}
            </span>
          </div>
        )}

        {data && (
          <div className="flex flex-col gap-4">
            {/* Summary stats */}
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
              {[
                {
                  label: "Genauigkeit",
                  value: `${Math.round(data.accuracy * 100)}%`,
                  color: "var(--risk-low)",
                },
                {
                  label: "Präzision",
                  value: `${Math.round(data.precision * 100)}%`,
                  color: "var(--accent-gold)",
                },
                {
                  label: "Recall",
                  value: `${Math.round(data.recall * 100)}%`,
                  color: "var(--accent)",
                },
                {
                  label: "Abgänger",
                  value: `${data.positiveSamples} / ${data.totalSamples}`,
                  color: "var(--text-secondary)",
                },
              ].map(({ label, value, color }) => (
                <div
                  key={label}
                  className="card px-4 py-3"
                  style={{ borderLeft: `2px solid ${color}` }}
                >
                  <div
                    className="text-[9px] uppercase tracking-widest font-semibold mb-1"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {label}
                  </div>
                  <div className="text-xl font-bold font-mono-data" style={{ color }}>
                    {value}
                  </div>
                </div>
              ))}
            </div>

            {/* Coefficient comparison table */}
            <div className="card overflow-hidden">
              <div
                className="px-4 py-3 flex items-center gap-2"
                style={{ borderBottom: "1px solid var(--border)", background: "var(--bg-surface)" }}
              >
                <FlaskConical size={13} style={{ color: "var(--accent)" }} />
                <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
                  Koeffizientenvergleich
                </span>
                <span className="text-xs ml-auto" style={{ color: "var(--text-muted)" }}>
                  Regress. vs. Heuristik — Übereinstimmung zeigt Modellkonsistenz
                </span>
              </div>
              <div>
                {/* Header */}
                <div
                  className="grid px-4 py-2 text-[9px] font-semibold uppercase tracking-widest"
                  style={{
                    gridTemplateColumns: "1fr 120px 120px 80px",
                    color: "var(--text-muted)",
                    borderBottom: "1px solid var(--border)",
                  }}
                >
                  <span>Signal</span>
                  <span>Heuristik</span>
                  <span>Regression</span>
                  <span>Richtung</span>
                </div>
                {signals
                  .sort((a, b) => (BASE_WEIGHTS[b] ?? 0) - (BASE_WEIGHTS[a] ?? 0))
                  .map((key) => {
                    const base = BASE_WEIGHTS[key] ?? 0;
                    const coeff = coefficients[key] ?? 0;
                    const normCoeff = Math.abs(coeff) / maxCoeff;
                    const agreement = (base > 0 && coeff > 0) || (base < 0 && coeff < 0);
                    const label = SIGNAL_LABELS[key] ?? key;
                    return (
                      <div
                        key={key}
                        className="grid items-center px-4 py-2.5"
                        style={{
                          gridTemplateColumns: "1fr 120px 120px 80px",
                          borderBottom: "1px solid var(--border)",
                        }}
                      >
                        <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                          {label}
                        </span>
                        <div className="flex items-center gap-2">
                          <div
                            className="flex-1 h-1.5 rounded-full"
                            style={{ background: "var(--border)" }}
                          >
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${base * 300}%`,
                                background: "var(--accent)",
                                maxWidth: "100%",
                              }}
                            />
                          </div>
                          <span
                            className="text-[10px] font-mono-data w-8 text-right"
                            style={{ color: "var(--text-muted)" }}
                          >
                            {base.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div
                            className="flex-1 h-1.5 rounded-full"
                            style={{ background: "var(--border)" }}
                          >
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${normCoeff * 100}%`,
                                background: agreement ? "var(--risk-low)" : "var(--risk-high)",
                                maxWidth: "100%",
                              }}
                            />
                          </div>
                          <span
                            className="text-[10px] font-mono-data w-10 text-right"
                            style={{ color: coeff > 0 ? "var(--risk-high)" : "var(--risk-low)" }}
                          >
                            {coeff.toFixed(3)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {agreement ? (
                            <span
                              className="text-[10px] flex items-center gap-1"
                              style={{ color: "var(--risk-low)" }}
                            >
                              <TrendingUp size={10} /> Konsistent
                            </span>
                          ) : (
                            <span
                              className="text-[10px] flex items-center gap-1"
                              style={{ color: "var(--risk-medium)" }}
                            >
                              <TrendingDown size={10} /> Abweichung
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <button
              type="button"
              className="flex items-center gap-1.5 text-xs px-3 py-2 rounded self-start"
              style={{ border: "1px solid var(--border)", color: "var(--text-muted)" }}
              onClick={() => void refetch()}
            >
              Training wiederholen
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
